import HomeIcon from '@mui/icons-material/Home'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'

import AddressAddComponent from 'components/address-add'
import AddressCardComponent from 'components/address-card'
import FloatingBackButtonComponent from 'components/button-floating-back'
import CardUserComponent from 'components/card-user'
import FilterComponent, { FilterSelectionProps } from 'components/filter'
import { FullScreenIcon, RightArrowIcon } from 'components/icons'
import SchoolLogo from 'components/logo-school'
import MapComponent from 'components/map'
import AfternoonMetricsComponent from 'components/metrics-afternoon'
import MorningMetricsComponent from 'components/metrics-morning'
import RadioMobileComponent from 'components/radio-mobile'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import { getDistance } from 'geolib'
import { useEffect, useMemo, useState } from 'react'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom'
import { LocationProps, MarkerProps, MessageProps } from 'types/CommonTypes'
import { CommunityCarpoolRequestOutputProps, DefaultCarpoolType, VehicleProps } from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { UserOutputProps } from 'types/UserTypes'
import { resolveScheduleTime } from 'utils/calendar'
import { getCommunityCarpoolRequests } from 'utils/communities'
import {
  METERS_TO_MILES,
  getLocalDate,
  getTimeString,
  isDateValid,
  isWeb,
  titleCase,
  titleCaseSchoolNameForUI,
} from 'utils/constants'
import { getMarker } from 'utils/map'
import { updateStudentAfternoonSettings, updateStudentMorningSettings } from 'utils/students'
import { Drawer } from 'vaul'
import MapUserIcon from '../../images/user_pin.svg'
import './index.css'
import { ExternalLinkIcon } from 'components/icons/ExternalLinkIcon'

const DashboardCarpoolComponent = ({
  user: { phoneNumber: myUserId },
  student: {
    studentId: myStudentId,
    schoolName,
    isSchoolSubscription,
    isStudentSubscription,
    schoolLocation,
    schoolStats,
    schoolScheduleGroups,
    schoolActivities: schoolActivitiesUnsorted,
    schoolPoolRadius,
    schoolAdminLink,
    scheduleGroupName,
    studentActivities,
    pickupLocation,
    dropoffLocation,
    dropoffTimeDefault,
    dropoffTimes,
  },
  failedAvatarUrls,
  onFailedAvatarUrl,
  date,
}: {
  user: UserOutputProps
  student: StudentProps
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
  date?: string
}): JSX.Element => {
  // 1. State Hooks
  const navigate: NavigateFunction = useNavigate()
  const [isAMPool, setIsAMPool] = useState<boolean>(date == null ? true : false) //assume AM if date query parameter absent
  const [schoolPoolStudents, setSchoolPoolStudents] = useState<CommunityCarpoolRequestOutputProps[]>()
  const [snap, setSnap] = useState<string | number | null>(0.68)
  const [openFullScreen, setOpenFullScreen] = useState<boolean>(false)
  const [centerMarker, setCenterMarker] = useState<MarkerProps>()
  const [markers, setMarkers] = useState<MarkerProps[]>()
  const [body, setBody] = useState<Record<string, any>[]>()
  const [selectedAMGroupNames, setSelectedAMGroupNames] = useState<Set<string>>(new Set())
  const [selectedAMVehicles, setSelectedAMVehicles] = useState<Set<string>>(new Set())
  const [selectedPMGroupNames, setSelectedPMGroupNames] = useState<Set<string>>(new Set())
  const [selectedPMVehicles, setSelectedPMVehicles] = useState<Set<string>>(new Set())
  // Only used for afternoons
  const [poolDate, setPoolDate] = useState<string>(date && isDateValid(date) ? date : getLocalDate()) // Only use valid dates
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set())
  // Generic states
  const [msg, setMsg] = useState<MessageProps>()
  const [isLoading, setIsLoading] = useState<string>('')

  // Navigation header component
  const schoolActivities = schoolActivitiesUnsorted?.sort() ?? []
  const schedules = useMemo(
    () => new Set(schoolScheduleGroups?.map(({ schoolScheduleGroupName }) => schoolScheduleGroupName) || []),
    [schoolScheduleGroups],
  )
  const vehicles = useMemo(() => new Set(['Using or seeking paid transport']), [])
  const amLocation: LocationProps = pickupLocation ?? { address: '' }
  const pmLocation: LocationProps = dropoffLocation ?? { address: '' }
  const isNeedAMPool: boolean = amLocation && amLocation?.lat && amLocation?.lng ? true : false
  const isNeedPMPool: boolean = pmLocation && pmLocation?.lat && pmLocation?.lng ? true : false
  const isEnabledAMPool: boolean = isAMPool && isNeedAMPool
  const isEnabledPMPool: boolean = !isAMPool && isNeedPMPool
  const isSubscribed: boolean = isSchoolSubscription || isStudentSubscription ? true : false
  const pmActivities = studentActivities?.sort() ?? []
  // Resolve dismissal time using fallback order:
  // 1. Custom dropoff time if set
  // 2. Default dropoff time if set
  // 3. School's default schedule
  const pmTime: string = poolDate
    ? getTimeString(
        resolveScheduleTime(
          'dismissal',
          schoolScheduleGroups,
          scheduleGroupName,
          dropoffTimeDefault,
          dropoffTimes?.[poolDate],
        ),
      )
    : ''
  // School name and home navigation component
  const NavigationHeader = () => {
    const handleSchoolClick = () => {
      if (schoolAdminLink) {
        window.open(schoolAdminLink, '_blank')
      }
    }

    return (
      <div className='flex justify-between items-center px-2 py-1.5 h-12 sm:h-14'>
        <div className='flex items-center gap-2 min-w-0 flex-1 h-full'>
          <div className='flex items-center gap-2 min-w-0 flex-1 h-full'>
            <SchoolLogo
              schoolName={schoolName}
              className='w-9 h-9 sm:w-10 sm:h-10 rounded-full shadow-sm object-cover flex-shrink-0'
            />
            <div className='min-w-0 flex-1'>
              <h1
                className='text-sm sm:text-base font-medium truncate text-[#1F2A37]'
                title={titleCaseSchoolNameForUI(schoolName)}
              >
                {titleCaseSchoolNameForUI(schoolName)}
              </h1>
              {schoolAdminLink ? (
                <button className='flex items-center gap-1 cursor-pointer' onClick={handleSchoolClick}>
                  <span className='text-[10px] sm:text-xs truncate'>Carpool Program</span>
                  <ExternalLinkIcon />
                </button>
              ) : (
                <span className='flex items-center gap-1 text-[10px] sm:text-xs truncate'>Carpool Program</span>
              )}
            </div>
          </div>
        </div>
        <div className='flex gap-1.5 sm:gap-2 flex-shrink-0 ml-1 sm:ml-2 h-full items-center'>
          <button
            className='flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-[#F0E977] hover:bg-[#E8E06F] transition-colors'
            onClick={() => navigate(`/hub?id=${myStudentId}`)}
            aria-label='Go to School Hub'
            data-testid='home-button'
          >
            <HomeIcon fontSize='small' style={{ fontSize: '1rem' }} />
          </button>
        </div>
      </div>
    )
  }

  // 3. Effect Hooks
  useEffect(() => {
    (async () => {
      setIsLoading('Loading carpool requests')
      const { data, msg, error } = await getCommunityCarpoolRequests(
        schoolName,
        isAMPool ? DefaultCarpoolType.ARRIVAL : DefaultCarpoolType.DISMISSAL,
        isAMPool ? undefined : poolDate,
      )
      if (error) setMsg(msg)
      else if (data) setSchoolPoolStudents(data.carpoolRequests as CommunityCarpoolRequestOutputProps[])
      setIsLoading('')
    })()
    window.history.replaceState(
      null,
      '',
      isAMPool ? `/carpool/dashboard?id=${myStudentId}` : `/carpool/dashboard?id=${myStudentId}&date=${poolDate}`,
    )
  }, [isAMPool, poolDate]) // eslint-disable-line

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (schoolPoolStudents && (isEnabledAMPool || isEnabledPMPool)) {
      // Only build cards if morning or afternoon active
      // Pool - Extract, Filter, Transform and Order
      const homeLocation: LocationProps = isAMPool ? amLocation : pmLocation
      const homeLat: number = homeLocation.lat as number
      const homeLng: number = homeLocation.lng as number
      const rows: any[] = [],
        pins: MarkerProps[] = []
      schoolPoolStudents.forEach((v: CommunityCarpoolRequestOutputProps, i: number) => {
        const {
          // User Props
          userId,
          userName,
          userRelationship,
          userPhotoLink,
          // Student Props
          studentId: userStudentId,
          scheduleGroupName,
          studentActivities,
          // Carpool Type Dependent Props
          position,
          vehicle,
          defaultTime,
          customTime,
        } = v
        const { lat, lng } = position ?? {}
        const isNotMyStudent: boolean = userId !== myUserId

        // Resolve dismissal time using fallback order:
        // 1. Custom dropoff time if set
        // 2. Default dropoff time if set
        // 3. School's default schedule
        const time: number = resolveScheduleTime(
          'dismissal',
          schoolScheduleGroups,
          scheduleGroupName,
          defaultTime,
          customTime,
        )

        const isScheduled = isAMPool || time >= 0
        const isMatchesActivityFilter =
          isAMPool || !selectedActivities.size || studentActivities?.find((v) => selectedActivities.has(v))
        const selectedGroupNames: Set<string> = isAMPool ? selectedAMGroupNames : selectedPMGroupNames
        const selectedVehicleProps: Set<string> = isAMPool ? selectedAMVehicles : selectedPMVehicles
        const isMatchesScheduleFilter = !selectedGroupNames.size || selectedGroupNames.has(scheduleGroupName)
        const isMatchesVehicleFilter = !selectedVehicleProps.size || vehicle === VehicleProps.HIRE_TRANSPORT_SERVICE

        // Filter out students not looking for carpool, and without matching filters
        if (
          position &&
          lat &&
          lng &&
          isNotMyStudent &&
          isScheduled &&
          isMatchesActivityFilter &&
          isMatchesScheduleFilter &&
          isMatchesVehicleFilter
        ) {
          const scheduleName: string | undefined =
            schoolScheduleGroups && schoolScheduleGroups?.length > 1 ? scheduleGroupName : undefined
          const relation: string = ['Parent/Stepparent', 'Mother', 'Father'].includes(userRelationship)
            ? 'Parent'
            : userRelationship
          const distance: number = getDistance({ lat: homeLat, lng: homeLng }, { lat, lng })
          const distanceMiles: string = Math.round(distance * METERS_TO_MILES * 10) / 10 + ' miles'
          const pmPickupTime: string | undefined = !isAMPool ? getTimeString(time) : undefined
          const activities: string[] | undefined =
            !isAMPool && Array.isArray(studentActivities) ? studentActivities : undefined
          let url: string = `/userpool/${schoolName}?studentId=${myStudentId}&userStudentId=${userStudentId}`
          url += isAMPool ? `&poolType=${DefaultCarpoolType.ARRIVAL}` : `&poolType=${DefaultCarpoolType.DISMISSAL}`
          const actions: JSX.Element = (
            <button
              className='flex w-10 h-10 justify-center items-center rounded-lg bg-[#F0E977]'
              aria-label='New Group'
              onClick={() => navigate(url, { state: { userId } })}
              style={{ marginLeft: 'auto' }} // Ensures the button is right-aligned
              data-testid='icon-button'
            >
              <RightArrowIcon />
            </button>
          )
          pins.push({
            position,
            iconPath: MapUserIcon,
            iconSize: { width: 45, height: 45 }, // Default size
            infoWindowId: `btn-${i}`,
            infoWindowContent: titleCase(userName),
            draggable: false,
          })
          rows.push({
            userName,
            userPhotoLink,
            distanceMiles,
            distance,
            relation,
            pmPickupTime,
            actions,
            activities,
            scheduleName,
          })
        }
      })
      setCenterMarker(getMarker(homeLocation, 'You', schoolPoolRadius))
      setMarkers([getMarker(schoolLocation, 'School'), ...pins])
      setBody(rows.sort((a, b) => (a.distance < b.distance ? -1 : 1)))
    } else {
      setCenterMarker(getMarker(schoolLocation, 'School'))
      setMarkers([])
      setBody([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schoolPoolStudents,
    selectedActivities,
    selectedAMGroupNames,
    selectedAMVehicles,
    selectedPMGroupNames,
    selectedPMVehicles,
  ])

  // 4. Derived State, Validations and Early Returns
  if (!centerMarker || !body) return <LinearProgress />
  const isNoResults: boolean = body.length === 0
  if (isLoading) return <LinearProgress />

  // 5. Event Handlers
  const handleSetSnap = (snapPoint: string | number | null) => {
    setSnap(snapPoint)
  }
  const handleFullScreen = () => {
    setOpenFullScreen((prev) => !prev)
  }
  const handleAMFilterSave = ({ scheduleSelections, vehicleSelections, clearAllSelections }: FilterSelectionProps) => {
    if (clearAllSelections) {
      setSelectedAMGroupNames(new Set())
      setSelectedAMVehicles(new Set())
      return
    }
    if (scheduleSelections) setSelectedAMGroupNames(scheduleSelections)
    if (vehicleSelections) setSelectedAMVehicles(vehicleSelections)
  }
  const handlePMFilterSave = ({
    scheduleSelections,
    activitySelections,
    vehicleSelections,
    dateSelection,
    clearAllSelections,
  }: FilterSelectionProps) => {
    if (clearAllSelections) {
      setSelectedPMGroupNames(new Set())
      setSelectedPMVehicles(new Set())
      setSelectedActivities(new Set())
      setPoolDate(getLocalDate())
      return
    }
    if (scheduleSelections) setSelectedPMGroupNames(scheduleSelections)
    if (vehicleSelections) setSelectedPMVehicles(vehicleSelections)
    if (activitySelections) setSelectedActivities(activitySelections)
    if (dateSelection) setPoolDate(dateSelection)
  }
  const handleMorningCarpoolDelete = async (): Promise<void> => {
    setIsLoading('Deleting morning carpool request')
    const { msg, error } = await updateStudentMorningSettings(schoolName, myStudentId, { address: '' })
    if (error) setMsg(msg)
    else navigate(0)
    setIsLoading('')
  }
  const handleAfternoonCarpoolDelete = async (): Promise<void> => {
    setIsLoading('Deleting afternoon carpool request')
    const { msg, error } = await updateStudentAfternoonSettings(schoolName, myStudentId, { address: '' })
    if (error) setMsg(msg)
    else navigate(0)
    setIsLoading('')
  }

  // 6. Render
  const mapElement: JSX.Element = (
    <>
      <MapComponent centerMarker={centerMarker} markers={markers} openFull={openFullScreen} maxZoom={15} />
      <button
        style={{
          backgroundColor: 'white',
          border: 'none',
          borderRadius: '50px',
          zIndex: 0,
          padding: '8px',
          position: isWeb ? 'absolute' : 'fixed',
          top: openFullScreen ? '93%' : isWeb ? '88%' : '30%',
          right: isWeb ? '2%' : '4%',
        }}
        onClick={handleFullScreen}
      >
        <FullScreenIcon />
      </button>
    </>
  )

  const radioElement: JSX.Element = (
    <>
      {/***************** Select Arrival vs Dismissal *****************/}
      <RadioMobileComponent
        row={true}
        radios={[
          {
            isChecked: isAMPool,
            label: <span>Morning</span>,
            onChange: (v: any) => {
              setIsAMPool(true)
            },
            data_testid: 'radio-morning-testid',
          },
          {
            isChecked: !isAMPool,
            label: <span>Afternoon</span>,
            onChange: (v: any) => {
              setIsAMPool(false)
            },
            data_testid: 'radio-afternoon-testid',
          },
        ]}
      />
    </>
  )

  const addressEntryElements: JSX.Element = (
    <>
      {/***************** Add Arrival and/or Dismissal Carpool Configuration *****************/}
      <AddressAddComponent
        id={myStudentId}
        schoolStats={schoolStats}
        showAM={isAMPool && !isNeedAMPool}
        showPM={!isAMPool && !isNeedPMPool}
      />

      {/***************** Update Arrival Carpool Configuration *****************/}
      {isEnabledAMPool && (
        <AddressCardComponent
          isAMPool={isAMPool}
          address={amLocation.address}
          onEdit={() => navigate(`/carpool/request/arrival?id=${myStudentId}`)}
          onDelete={handleMorningCarpoolDelete}
        />
      )}
      {/***************** Update Dismissal Carpool Configuration *****************/}
      {isEnabledPMPool && (
        <AddressCardComponent
          isAMPool={false}
          address={pmLocation.address}
          date={poolDate}
          time={pmTime}
          activities={pmActivities}
          onEdit={() => navigate(`/carpool/request/dismissal?id=${myStudentId}`)}
          onDelete={handleAfternoonCarpoolDelete}
        />
      )}
    </>
  )

  const filterConnectionStatsElements = (
    <>
      {/***************** Show Arrival Carpool Filter *****************/}
      {isEnabledAMPool && isSubscribed && (
        <div className='flex items-center gap-3'>
          <FilterComponent
            schedules={schedules}
            vehicles={vehicles}
            selectedSchedules={selectedAMGroupNames}
            selectedVehicles={selectedAMVehicles}
            onSave={handleAMFilterSave}
          />
        </div>
      )}

      {/***************** Show Dismissal Carpool Filter *****************/}
      {isEnabledPMPool && isSubscribed && (
        <div className='flex items-center gap-1'>
          <FilterComponent
            schedules={schedules}
            vehicles={vehicles}
            activities={new Set(schoolActivities?.map((v: string) => v) || [])}
            selectedActivities={selectedActivities}
            selectedDate={poolDate}
            selectedSchedules={selectedPMGroupNames}
            selectedVehicles={selectedPMVehicles}
            onSave={handlePMFilterSave}
          />
        </div>
      )}
      {/***************** Show Connections *****************/}
      {(isEnabledAMPool || isEnabledPMPool) && isSubscribed && (
        <div
          className={`${isWeb ? 'cards-container-web' : 'cards-container-mobile'} ${isNoResults ? (isWeb ? 'flex items-center justify-center' : '') : ''}`}
          data-testid='cards-container-testid'
        >
          {!isNoResults ? (
            body.map((row, index) => (
              <CardUserComponent
                key={index}
                isAMPool={isAMPool}
                isLast={index === body.length - 1}
                userName={row.userName}
                userRelationship={row.relation}
                photoLink={row.userPhotoLink}
                failedPhotoLinks={failedAvatarUrls}
                onFailedPhotoLink={onFailedAvatarUrl}
                scheduleName={row.scheduleName}
                distance={row.distanceMiles}
                time={row.pmPickupTime}
                activities={row.activities}
                action={row.actions}
              />
            ))
          ) : (
            <div className='flex flex-col items-center p-8 text-center max-w-md mx-auto'>
              <h2 className='text-xl font-semibold mb-2'>No Carpool Connections Found</h2>
              <p className='text-gray-600 mb-6'>
                Try adjusting your filters or check back later as more families join.
              </p>
            </div>
          )}
        </div>
      )}
      {/***************** Show Stats *****************/}
      {isEnabledAMPool && !isSubscribed && (
        <div
          className={`${isWeb ? 'scrollable-container-web metrics-container-web' : 'scrollable-container-mobile metrics-container-mobile'}`}
        >
          <MorningMetricsComponent schoolStats={schoolStats} />
        </div>
      )}
      {isEnabledPMPool && !isSubscribed && (
        <div
          className={`${isWeb ? 'scrollable-container-web metrics-container-web' : 'scrollable-container-mobile metrics-container-mobile'}`}
        >
          <AfternoonMetricsComponent schoolStats={schoolStats} />
        </div>
      )}
    </>
  )

  return (
    <>
      {/***************** Display progress and error *****************/}
      {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}

      {/***************** Mobile *****************/}
      {!isWeb && (
        <>
          <FloatingBackButtonComponent />
          {mapElement}
          {!openFullScreen && (
            <div data-vaul-drawer-wrapper='' style={{ zIndex: 10 }}>
              <Drawer.Root
                snapPoints={[0.68, 0.95]}
                activeSnapPoint={snap}
                setActiveSnapPoint={handleSetSnap}
                open={true}
                modal={false}
                dismissible={false}
                shouldScaleBackground
              >
                <Drawer.Portal>
                  <Drawer.Overlay className='fixed inset-0 bg-black/40' />
                  <Drawer.Content className='bg-zinc-100 flex flex-col rounded-t-[20px] h-[96%] mt-24 fixed bottom-0 left-0 right-0 '>
                    <div className='p-4 bg-white rounded-t-[20px] flex-1'>
                      <div className='mx-auto w-16 h-1.5 flex-shrink-0 rounded-full bg-zinc-300' />
                      <div className='max-w-[768px] mx-auto'>
                        <NavigationHeader />
                        <Drawer.Title className='font-medium mb-4'>
                          <Paper
                            variant='outlined'
                            sx={{
                              my: 1,
                              display: 'flex',
                              border: 'none',
                              gap: '10px',
                              justifyContent: 'space-between',
                              width: '100%',
                              background: '#F9F9F9',
                              padding: '8px',
                              borderRadius: '12px',
                            }}
                          >
                            {radioElement}
                          </Paper>

                          {addressEntryElements}
                        </Drawer.Title>
                        {filterConnectionStatsElements}
                      </div>
                    </div>
                    <div className='p-4 bg-zinc-100 border-t border-zinc-200 mt-auto'>
                      <div className='flex gap-6 justify-end max-w-md mx-auto'></div>
                    </div>
                  </Drawer.Content>
                </Drawer.Portal>
              </Drawer.Root>
            </div>
          )}
        </>
      )}
      {/***************** Web *****************/}
      {isWeb && (
        <WizardComponent title={''}>
          <div style={{ position: 'relative' }}>
            <NavigationHeader />
            {mapElement}
          </div>
          {!openFullScreen && (
            <div data-vaul-drawer-wrapper='' style={{ zIndex: 10 }} className='bg-white'>
              {/***************** Select Arrival vs Dismissal *****************/}
              <div className='radio-buttons-web'>{radioElement}</div>

              {addressEntryElements}

              <br />
              {filterConnectionStatsElements}
            </div>
          )}
        </WizardComponent>
      )}
    </>
  )
}

function Index(props: {
  user?: UserOutputProps
  students: StudentProps[]
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}): JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Inputs: Tokens, Students, Selected Student Id, and Callback
  const { user, students, failedAvatarUrls, onFailedAvatarUrl } = props

  // Build props for child component
  const studentId = searchParams.get('id') ?? ''
  const date = searchParams.get('date')
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  useEffect(() => {
    if (!user) return
    if (!student) navigate(`/hub?id=${studentId}`)
  }, [user, student, navigate, studentId])

  if (!user || !studentId || !student) return <LinearProgress />

  return (
    <DashboardCarpoolComponent
      user={user}
      student={student}
      failedAvatarUrls={failedAvatarUrls}
      onFailedAvatarUrl={onFailedAvatarUrl}
      date={date ? date : undefined}
    />
  )
}

export default Index
